import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../../../App.css";
import { getBlogAuthor } from "./blogAuthors";

const BlogIndex = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [author, setAuthor] = useState("all");
  const [sortOrder, setSortOrder] = useState("newest");

  useEffect(() => {
    let mounted = true;

    const loadPosts = async () => {
      try {
        const response = await fetch("/api/blog/posts", {
          credentials: "include",
          cache: "no-store",
        });

        const payload = await response.json().catch(() => []);

        if (!response.ok) {
          throw new Error(payload?.error || "Unable to load blog posts");
        }

        if (mounted) setPosts(Array.isArray(payload) ? payload : []);
      } catch (err) {
        if (mounted) setError(err.message || "Unable to load blog posts");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadPosts();

    return () => {
      mounted = false;
    };
  }, []);

  const categories = useMemo(
    () => Array.from(new Set(posts.map((post) => post.category).filter(Boolean))).sort(),
    [posts]
  );

  const authors = useMemo(
    () => Array.from(new Set(posts.map((post) => post.author).filter(Boolean))).sort(),
    [posts]
  );

  const visiblePosts = useMemo(() => {
    const query = search.trim().toLowerCase();

    return posts
      .filter((post) => {
        const matchesSearch = !query || [post.title, post.excerpt, post.category, post.author]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));

        const matchesCategory = category === "all" || post.category === category;
        const matchesAuthor = author === "all" || post.author === author;

        return matchesSearch && matchesCategory && matchesAuthor;
      })
      .sort((a, b) => {
        const direction = sortOrder === "newest" ? -1 : 1;
        return direction * String(a.date || "").localeCompare(String(b.date || ""));
      });
  }, [posts, search, category, author, sortOrder]);

  return (
    <div className="blog-shell">
      <div className="blog-panel">
        <div className="blog-topbar">
          <div>
            <div className="blog-kicker">POGFORTS ARCHIVE</div>
            <h1 className="blog-title">Blog</h1>
            <p className="blog-subtitle">
              Permanent dispatches, public records, and Pogforts field notes.
            </p>
          </div>
        </div>

        <div className="blog-controls" aria-label="Blog filters">
          <input
            className="blog-control"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search title, text, author, category..."
          />

          <select
            className="blog-control"
            value={category}
            onChange={(event) => setCategory(event.target.value)}
          >
            <option value="all">All categories</option>
            {categories.map((entry) => (
              <option key={entry} value={entry}>{entry}</option>
            ))}
          </select>

          <select
            className="blog-control"
            value={author}
            onChange={(event) => setAuthor(event.target.value)}
          >
            <option value="all">All authors</option>
            {authors.map((entry) => (
              <option key={entry} value={entry}>{entry}</option>
            ))}
          </select>

          <select
            className="blog-control"
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value)}
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
        </div>

        {loading ? <p className="blog-loading">Loading blog posts...</p> : null}
        {error ? <p className="blog-error">{error}</p> : null}

        {!loading && !error && visiblePosts.length === 0 ? (
          <p className="blog-empty">No matching posts found.</p>
        ) : null}

        <div className="blog-grid">
          {visiblePosts.map((post) => {
            const postAuthor = getBlogAuthor(post.author);

            return (
              <article className="blog-card" key={post.slug}>
                <img src={post.imageUrl} alt="" className="blog-card-image" loading="lazy" />

                <div className="blog-card-body">
                  <div className="blog-card-category">{post.category}</div>
                  <h2 className="blog-card-title">{post.title}</h2>

                  <div className="blog-card-meta">
                    <img src={postAuthor.avatar} alt="" className="blog-author-avatar" />
                    <div>
                      <div className="blog-card-author">{postAuthor.label}</div>
                      <div className="blog-card-date">{post.date}</div>
                    </div>
                  </div>

                  <p className="blog-card-excerpt">{post.excerpt}</p>

                  <div className="blog-card-actions">
                    <Link to={`/blog/${post.slug}`} className="blog-button">Read</Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BlogIndex;
